import { Injectable } from '@angular/core';
import { NodeService } from './node.service';
import { RecorderRuntimeService } from './recorder-runtime.service';

export interface IngameOverlayRuntimeOptions {
    enabled: () => boolean;
    overlayUrl: string;
    production: boolean;
}

/** Owns the native in-game window independently from the dashboard view. */
@Injectable({ providedIn: 'root' })
export class IngameOverlayRuntimeService {
    private options: IngameOverlayRuntimeOptions | null = null;
    private overlayWindow: Electron.BrowserWindow | null = null;
    private waitTimer: any = null;
    private reloadTimer: any = null;
    private generation = 0;
    private closingIntentionally = false;
    private lookupInFlight = false;

    constructor(
        private readonly node: NodeService,
        private readonly recorderRuntime: RecorderRuntimeService
    ) { }

    configure(options: IngameOverlayRuntimeOptions): void {
        this.options = options;
        this.adoptExistingWindow();
        this.reconcile();
    }

    reconcile(): void {
        if (!this.isEnabled()) {
            this.close();
            return;
        }
        this.ensureWindow();
    }

    restart(): void {
        this.close();
        if (this.isEnabled()) this.ensureWindow();
    }

    close(): void {
        this.generation++;
        this.clearWaitTimer();
        this.clearReloadTimer();
        if (!this.overlayWindow) {
            this.closingIntentionally = false;
            return;
        }
        this.closingIntentionally = true;
        this.overlayWindow.close();
        this.overlayWindow = null;
    }

    private ensureWindow(): void {
        if (!this.node.isWindowsHost() || this.overlayWindow || this.waitTimer || !this.isEnabled()) return;
        const generation = ++this.generation;
        this.waitTimer = setInterval(() => {
            if (generation !== this.generation || !this.isEnabled()) {
                this.clearWaitTimer();
                return;
            }
            if (this.lookupInFlight) return;
            this.lookupInFlight = true;
            this.recorderRuntime.getWarcraftWindow().then(warcraftWindow => {
                if (generation !== this.generation || !this.isEnabled()) return;
                this.clearWaitTimer();
                this.createWindow(warcraftWindow);
            }).catch(() => null).finally(() => this.lookupInFlight = false);
        }, 1000);
    }

    private createWindow(warcraftWindow: number): void {
        if (this.overlayWindow || !this.options || !this.isEnabled()) return;
        const user32 = this.node.ffi.Library('user32.dll', {
            SetParent: ['long', ['long', 'long']]
        });
        const overlayWindow = new this.node.remote.BrowserWindow({
            title: 'W3Booster Ingame Overlay',
            frame: false,
            transparent: true,
            focusable: false,
            show: false,
            webPreferences: {
                nodeIntegration: true,
                contextIsolation: false,
                backgroundThrottling: false
            }
        });
        (overlayWindow as any).__w3boosterIngameOverlay = true;
        this.overlayWindow = overlayWindow;
        overlayWindow.on('page-title-updated', event => {
            event.preventDefault();
            overlayWindow.setTitle('W3Booster Ingame Overlay');
        });
        const nativeWindow = this.node.ref.types.int64.get(overlayWindow.getNativeWindowHandle(), 0);
        overlayWindow.setSize(1920, 1080);
        overlayWindow.setIgnoreMouseEvents(true, { forward: false });
        // Input ownership and its watchdog live in the desktop main process,
        // independently from the compositor and third-party app renderers.
        overlayWindow.webContents.on('did-finish-load', () => {
            if (overlayWindow.isDestroyed()) return;
            this.clearReloadTimer();
            overlayWindow.webContents.insertCSS('html,body{background-color:transparent !important;}');
            overlayWindow.showInactive();
        });
        overlayWindow.webContents.on('did-fail-load', (_event, errorCode, _description, _url, isMainFrame) => {
            // Chromium reports -3 for intentional navigation cancellation.
            if (errorCode === -3 || isMainFrame === false) return;
            this.scheduleReload(overlayWindow);
        });
        overlayWindow.on('closed', () => {
            this.clearReloadTimer();
            if (this.overlayWindow === overlayWindow) this.overlayWindow = null;
            const shouldReopen = !this.closingIntentionally && this.isEnabled();
            this.closingIntentionally = false;
            if (shouldReopen) this.ensureWindow();
        });
        const url = `${this.options.overlayUrl}&w3hwnd=${warcraftWindow}`;
        const loadRegisteredWindow = (registered: boolean) => {
            if (overlayWindow.isDestroyed()) return;
            // New hosts attach an owned popup. Only legacy hosts use SetParent;
            // temporarily parenting a new popup can attach native input queues.
            if (!registered) user32.SetParent(nativeWindow, warcraftWindow);
            this.loadWindow(overlayWindow, url);
        };
        this.node.ipcRenderer.invoke('overlay-input:register', overlayWindow.id, warcraftWindow)
            .then(registered => loadRegisteredWindow(registered === true), () => loadRegisteredWindow(false));
    }

    private isEnabled(): boolean {
        return !!this.options?.enabled();
    }

    private adoptExistingWindow(): void {
        if (!this.node.isAvailable() || this.overlayWindow) return;
        let windows: any[] = [];
        try {
            windows = this.node.remote.BrowserWindow.getAllWindows().filter(window =>
                !window.isDestroyed() && (
                    (window as any).__w3boosterIngameOverlay ||
                    window.getTitle() === 'W3Booster Ingame Overlay' ||
                    this.isIngameCompositorUrl(window.webContents?.getURL?.())
                )
            );
        } catch (_) {
            return;
        }
        this.overlayWindow = windows.shift() || null;
        for (const duplicate of windows) duplicate.close();
        if (!this.overlayWindow) return;
        const adoptedWindow = this.overlayWindow;
        try {
            const handle = Number(new URL(adoptedWindow.webContents.getURL()).searchParams.get('w3hwnd'));
            if (handle > 0) this.node.ipcRenderer.invoke('overlay-input:register', adoptedWindow.id, handle).catch(() => undefined);
        } catch (_) { /* Unknown legacy windows retain click-through. */ }
        // A compositor outage can leave a persistent native window displaying
        // Chromium's failed-load page. Retry it when the Angular renderer is
        // recreated instead of treating the blank window as healthy.
        try { adoptedWindow.webContents.reloadIgnoringCache(); }
        catch (_) { /* A closing window will be reconciled below. */ }
        adoptedWindow.once('closed', () => {
            if (this.overlayWindow === adoptedWindow) this.overlayWindow = null;
            if (this.isEnabled()) this.ensureWindow();
        });
    }

    private clearWaitTimer(): void {
        if (!this.waitTimer) return;
        clearInterval(this.waitTimer);
        this.waitTimer = null;
    }

    private loadWindow(overlayWindow: Electron.BrowserWindow, targetUrl: string): void {
        Promise.resolve(overlayWindow.loadURL(targetUrl, { extraHeaders: 'pragma: no-cache\n' }))
            .catch(() => this.scheduleReload(overlayWindow, targetUrl));
    }

    private scheduleReload(overlayWindow: Electron.BrowserWindow, targetUrl?: string): void {
        if (this.reloadTimer || overlayWindow.isDestroyed() || !this.isEnabled()) return;
        this.reloadTimer = setTimeout(() => {
            this.reloadTimer = null;
            if (overlayWindow.isDestroyed() || this.overlayWindow !== overlayWindow || !this.isEnabled()) return;
            if (targetUrl) this.loadWindow(overlayWindow, targetUrl);
            else overlayWindow.webContents.reloadIgnoringCache();
        }, 1000);
    }

    private clearReloadTimer(): void {
        if (!this.reloadTimer) return;
        clearTimeout(this.reloadTimer);
        this.reloadTimer = null;
    }

    private isIngameCompositorUrl(value: string): boolean {
        try { return new URL(String(value || '')).searchParams.has('w3hwnd'); }
        catch (_) { return false; }
    }
}
